// src/pages/SongUploadTwo.jsx
import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { db, storage } from "../firebaseConfig";

/**
 * Esta pantalla recibe usuarioId y cancionId por:
 * - state (recomendado) desde navigate()
 * - o params en URL como respaldo
 *
 * Rutas sugeridas:
 * /admin/upload-two/:usuarioId/:cancionId
 */
export default function SongUploadTwo() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // 1) Recibir ids desde state o params
  const usuarioId = location.state?.usuarioId || params.usuarioId || "";
  const cancionId = location.state?.cancionId || params.cancionId || "";

  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);

  const [subiendo, setSubiendo] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const input1Ref = useRef(null);
  const input2Ref = useRef(null);

  const valido = useMemo(() => {
    return Boolean(usuarioId && cancionId && file1 && file2);
  }, [usuarioId, cancionId, file1, file2]);

  const isMp3 = (file) => {
    if (!file) return false;
    const nameOk = file.name?.toLowerCase().endsWith(".mp3");
    const typeOk = file.type === "audio/mpeg" || file.type === "audio/mp3";
    // algunos navegadores no ponen type, por eso nameOk también ayuda
    return nameOk || typeOk;
  };

  const onPick = (slot, file) => {
    setMsg("");
    setError("");

    if (!file) return;
    if (!isMp3(file)) {
      setError("Solo se permiten archivos .mp3");
      return;
    }
    if (slot === 1) setFile1(file);
    if (slot === 2) setFile2(file);
  };

  const onDrop = (slot, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    onPick(slot, file);
  };

  const prevent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const subirYActualizar = async () => {
    setError("");
    setMsg("");

    if (!usuarioId || !cancionId) {
      setError("Faltan usuarioId o cancionId (no llegaron desde la pantalla anterior).");
      return;
    }
    if (!file1 || !file2) {
      setError("Selecciona los 2 archivos mp3.");
      return;
    }

    setSubiendo(true);
    try {
      // 2) Subir a Storage
      // Ruta solicitada: usuarios/usuarioid/cancionid...mp3
      // Usamos nombres estables para no pisarnos sin querer
      const path1 = `usuarios/${usuarioId}/${cancionId}_01.mp3`;
      const path2 = `usuarios/${usuarioId}/${cancionId}_02.mp3`;

      const r1 = ref(storage, path1);
      const r2 = ref(storage, path2);

      await uploadBytes(r1, file1, { contentType: "audio/mpeg" });
      await uploadBytes(r2, file2, { contentType: "audio/mpeg" });

      // 3) Obtener URLs
      const url1 = await getDownloadURL(r1);
      const url2 = await getDownloadURL(r2);

      // 4) Actualizar Firestore
      // usuarios/{userId}/canciones/{cancionId}
      const songRef = doc(db, "usuarios", usuarioId, "canciones", cancionId);

      await updateDoc(songRef, {
        coverUrl: url1,
        coverUrl02: url2,
        updatedAt: new Date(),
      });

      setMsg("✅ Subida con éxito y usuario actualizado.");
    } catch (err) {
      console.error(err);
      setError("Ocurrió un error al subir/actualizar. Revisa consola y permisos de Firebase.");
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Subir 2 canciones (mp3)</h2>
        <button onClick={() => navigate(-1)} style={{ padding: "8px 12px", borderRadius: 8 }}>
          Volver
        </button>
      </div>

      <div style={{ marginTop: 8, opacity: 0.85 }}>
        <div><b>usuarioId:</b> {usuarioId || "—"}</div>
        <div><b>cancionId:</b> {cancionId || "—"}</div>
      </div>

      {/* Dropzones */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        {/* Slot 1 */}
        <div
          onDragEnter={prevent}
          onDragOver={prevent}
          onDrop={(e) => onDrop(1, e)}
          style={{
            border: "2px dashed #bbb",
            borderRadius: 12,
            padding: 16,
            minHeight: 180,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Archivo 1</h3>
          <p style={{ marginTop: 0, opacity: 0.8 }}>
            Arrastra el mp3 aquí o selecciónalo.
          </p>

          <input
            ref={input1Ref}
            type="file"
            accept=".mp3,audio/mpeg"
            style={{ display: "none" }}
            onChange={(e) => onPick(1, e.target.files?.[0])}
          />

          <button
            onClick={() => input1Ref.current?.click()}
            style={{ padding: "10px 12px", borderRadius: 10 }}
          >
            Buscar archivo
          </button>

          <div style={{ marginTop: 12 }}>
            <b>Seleccionado:</b>{" "}
            {file1 ? file1.name : <span style={{ opacity: 0.7 }}>ninguno</span>}
          </div>
        </div>

        {/* Slot 2 */}
        <div
          onDragEnter={prevent}
          onDragOver={prevent}
          onDrop={(e) => onDrop(2, e)}
          style={{
            border: "2px dashed #bbb",
            borderRadius: 12,
            padding: 16,
            minHeight: 180,
          }}
        >
          <h3 style={{ marginTop: 0 }}>Archivo 2</h3>
          <p style={{ marginTop: 0, opacity: 0.8 }}>
            Arrastra el mp3 aquí o selecciónalo.
          </p>

          <input
            ref={input2Ref}
            type="file"
            accept=".mp3,audio/mpeg"
            style={{ display: "none" }}
            onChange={(e) => onPick(2, e.target.files?.[0])}
          />

          <button
            onClick={() => input2Ref.current?.click()}
            style={{ padding: "10px 12px", borderRadius: 10 }}
          >
            Buscar archivo
          </button>

          <div style={{ marginTop: 12 }}>
            <b>Seleccionado:</b>{" "}
            {file2 ? file2.name : <span style={{ opacity: 0.7 }}>ninguno</span>}
          </div>
        </div>
      </div>

      {/* Mensajes */}
      {error && (
        <div style={{ marginTop: 14, color: "crimson", fontWeight: 600 }}>
          {error}
        </div>
      )}
      {msg && (
        <div style={{ marginTop: 14, color: "green", fontWeight: 700 }}>
          {msg}
        </div>
      )}

      {/* Acción */}
      <div style={{ marginTop: 18, display: "flex", gap: 10, alignItems: "center" }}>
        <button
          disabled={!valido || subiendo}
          onClick={subirYActualizar}
          style={{
            padding: "12px 14px",
            borderRadius: 10,
            border: 0,
            cursor: valido && !subiendo ? "pointer" : "not-allowed",
          }}
        >
          {subiendo ? "Subiendo..." : "Subir y actualizar"}
        </button>

        <div style={{ opacity: 0.75 }}>
          Esto guardará URLs en <b>coverUrl</b> y <b>coverUrl02</b>.
        </div>
      </div>
    </div>
  );
}
